/**
 * „Neuen Lauf starten“ (28.10.2026) — was Seite (frontend/lib/lauf-start.ts) und Server (server/lauf-starten-route.ts) gemeinsam wissen.
 *
 * Martin sah keinen Knopf, wenn Vespera die Mandats-Karte nicht legte. Jetzt tippt (oder diktiert) er seinen Auftrag selbst in die
 * Lauf-Leiste, wählt die Art und tippt „Karte anzeigen“. Der Server legt dann die VORHANDENE Mandats-Karte an (server/prozess-freigabe.ts
 * `mandatKarteAnlegen`) — mit SEINEM Wortlaut. Das Mandat entsteht weiter allein durch seinen Tipp auf einen Stufen-Knopf der Karte.
 *
 * ⚠️ Das Feld wird nie vorbelegt (kein Text vom Server, vom Modell, aus dem Verlauf). Hier stehen nur feste Wörter und Prüfregeln.
 */
import { MANDAT_ART_TITEL, MANDAT_ARTEN, type MandatArt } from "./prozess-freigabe.js";
import { enthaeltZugangswert } from "./zugangswert.js";

export const LAUF_STARTEN_PFAD = "/api/lauf/starten";
/** Wert des Kopfs der eigenen Seite (Kopfname wie beim Update-Knopf: shared/update-starten.ts `UPDATE_KOPF`). */
export const LAUF_STARTEN_KOPF_WERT = "lauf-starten";

export const AUFGABE_MIN = 10;
export const AUFGABE_MAX = 600;

/** Woher der Wortlaut kommt: Martins Tastatur oder seine Diktierfunktion im Feld. Alles andere (Modell, Weckruf, Fremdtext) nie. */
export const LAUF_START_QUELLEN = ["tippen", "diktat"] as const;
export type LaufStartQuelle = (typeof LAUF_START_QUELLEN)[number];
export const istLaufStartQuelle = (v: unknown): v is LaufStartQuelle =>
  typeof v === "string" && (LAUF_START_QUELLEN as readonly string[]).includes(v);

/** Die Arten in der Reihenfolge der Auswahl — Titel aus der festen Tabelle der Karte. */
export const LAUF_START_ARTEN: readonly { art: MandatArt; titel: string }[] = Object.freeze(
  // „soniox-einrichtung“ erscheint hier nicht (Schalter MANDAT_ART_SONIOX liegt am Server): Martin wählt sie über Vespera (mandat_anfragen).
  MANDAT_ARTEN.filter((art) => art !== "soniox-einrichtung").map((art) => Object.freeze({ art, titel: MANDAT_ART_TITEL[art] })),
);

/** Warum der Wortlaut nicht geht — oder `null`: er geht. */
export type AufgabeNein = "leer" | "kurz" | "lang" | "zeichen" | "zugangswert";

/**
 * Steuer- und Formatzeichen, die im Wortlaut nichts zu suchen haben. Zeilenumbruch und Tabulator aus dem Feld sind erlaubt (die Karte
 * zeigt den Wortlaut einzeilig). Dazu Richtungs- und Nullbreite-Zeichen: sie verstecken Text vor dem Auge.
 */
// eslint-disable-next-line no-control-regex
const STEUERZEICHEN = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f-\u009f\u00ad\u061c\u180e\u200b-\u200f\u2028-\u202e\u2060-\u2064\u2066-\u206f\ufeff\ufff9-\ufffb]/u;

/** Prüft Martins Wortlaut: Länge 10–600 (nach Trimmen), keine Steuerzeichen, kein Zugangswert (Passwort, Schlüssel …). */
export function aufgabeNein(text: unknown): AufgabeNein | null {
  if (typeof text !== "string") return "leer";
  const t = text.trim();
  if (!t) return "leer";
  if (STEUERZEICHEN.test(t)) return "zeichen";
  const laenge = [...t].length;
  if (laenge < AUFGABE_MIN) return "kurz";
  if (laenge > AUFGABE_MAX) return "lang";
  if (enthaeltZugangswert(t)) return "zugangswert";
  return null;
}

/** Fehlerkürzel der Route — die Seite zeigt dazu den festen Satz. */
export type LaufStartFehler =
  | AufgabeNein
  | "aus"
  | "not-logged-in"
  | "auth-revoked"
  | "konto"
  | "herkunft"
  | "felder"
  | "quelle"
  | "art"
  | "laeuft"
  | "beschaeftigt"
  | "limit"
  | "nicht-bereit";

export const LAUF_START_SATZ: Readonly<Record<LaufStartFehler, string>> = Object.freeze({
  leer: "Schreib zuerst, was ich tun soll.",
  kurz: `Der Auftrag braucht mindestens ${AUFGABE_MIN} Zeichen.`,
  lang: `Der Auftrag darf höchstens ${AUFGABE_MAX} Zeichen lang sein.`,
  zeichen: "Im Auftrag stehen unsichtbare Steuerzeichen. Schreib ihn bitte neu.",
  zugangswert: "Im Auftrag steht etwas wie ein Passwort oder Schlüssel. Nimm es heraus – Zugangsdaten gibst du selbst ein.",
  aus: "Neue Läufe sind gerade abgeschaltet.",
  "not-logged-in": "Bitte melde dich neu an.",
  "auth-revoked": "Bitte melde dich neu an.",
  konto: "Einen Lauf gibt es nur für das Hauptkonto.",
  herkunft: "Das ging nicht von dieser Seite aus. Lade die Seite neu.",
  felder: "Die Anfrage war unvollständig. Lade die Seite neu.",
  quelle: "Einen Lauf startest nur du selbst, hier im Feld.",
  art: "Wähle eine Art aus der Liste.",
  laeuft: "Es läuft schon ein Lauf. Stoppe ihn zuerst oder tippe auf der Karte im Chat „Beenden“.",
  beschaeftigt: "Vespera arbeitet gerade. Versuch es gleich noch einmal, wenn sie fertig ist.",
  limit: "Zu viele Versuche in der letzten Stunde. Versuch es später noch einmal.",
  "nicht-bereit": "Die Karte ließ sich gerade nicht anlegen. Versuch es noch einmal.",
});

export function istLaufStartFehler(v: unknown): v is LaufStartFehler {
  return typeof v === "string" && Object.hasOwn(LAUF_START_SATZ, v);
}
